import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import {
  getGetApiV5FirmsRfiRequestsUuidQueryKey,
  usePostApiV5FirmsRfiRequestsUuidArchive,
  parseApiErrorMessage,
} from '@liscio/api';
import { Dialog, Typography } from '@liscio/ui';

export interface RfiArchiveDialogProps {
  open: boolean;
  requestUuid: string;
  onClose: () => void;
  onSuccessfulArchive?: () => void;
}

/**
 * Dialog to confirm archiving an RFI request from the detail header.
 */
export const RfiArchiveDialog: React.FC<RfiArchiveDialogProps> = ({
  open,
  requestUuid,
  onClose,
  onSuccessfulArchive,
}) => {
  const queryClient = useQueryClient();

  const { mutateAsync: archiveRequest, isLoading } =
    usePostApiV5FirmsRfiRequestsUuidArchive({
      mutation: {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ['/api/v5/firms/rfi/requests'],
          });
          queryClient.invalidateQueries({
            queryKey: getGetApiV5FirmsRfiRequestsUuidQueryKey(requestUuid),
          });
          toast.success('Request archived successfully.');
          onSuccessfulArchive?.();
        },
        onError: (error) => {
          const errorMessage =
            parseApiErrorMessage(error) || 'Error archiving request';
          toast.error(errorMessage);
          console.error('Error archiving request:', error);
        },
        onSettled: () => {
          onClose();
        },
      },
    });

  const handleArchive = () => {
    archiveRequest({ uuid: requestUuid });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Archive this request?"
      iconClose
      onSubmit={handleArchive}
      submitLabel="Yes, archive"
      submitBtnProps={{ disabled: isLoading, sx: { backgroundColor: 'grey.700', '&:hover': { backgroundColor: 'grey.800' } } }}
      cancelLabel="No, cancel"
      onCancel={onClose}
      cancelBtnProps={{ color: 'primary', disabled: isLoading }}
      aria-label="Archive request dialog"
    >
      <Typography>
        Are you sure you want to archive this request? Once archived, the
        request cannot be resent.
      </Typography>
    </Dialog>
  );
};

export default RfiArchiveDialog;
